import { HelpOutlineOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import type { Dispatch, FC, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { FONT_SIZE, ICON_SIZE, INK } from "../../../../theme/tokens";
import { SUBGROUP_BORDERED_SX_BASE } from "./ratesConsts";
import { ShiftTimesEditor } from "./ShiftTimesEditor";
import type { GeneralFormData } from "./types";

/**
 * The `default` shift-times bucket — what every picker resolving to neither a job nor a role uses.
 *
 * It had a row in the old Shift Lengths table, which was keyed on "whatever a picker resolves". The
 * Jobs grid that absorbed that table is keyed on JOBS, and `default` is not one — a row there would
 * need a fake id and carry no group, no PPD inclusion and no removal (DAT-804). Hence a card.
 *
 * Mode-independent: it always writes the legacy role-keyed maps, so its scope stays `role`.
 */
export const ShiftTimesFallbackCard: FC<{
	formData: GeneralFormData;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
	hourOptions: string[];
}> = ({ formData, setFormData, hourOptions }) => {
	const { t } = useTranslation();

	return (
		<Box sx={{ ...SUBGROUP_BORDERED_SX_BASE, display: "flex", flexDirection: "column", gap: 1, p: 2 }} data-testid="dept-config-shifts-default-card">
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
				<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY }}>{t("department.config.shifts.default.title")}</Typography>
				<InHouseTooltip title={t("department.config.shifts.default.description")}>
					<HelpOutlineOutlined
						tabIndex={0}
						aria-label={t("department.config.shifts.default.description")}
						sx={{ fontSize: ICON_SIZE.MICRO, color: INK.MUTED }}
					/>
				</InHouseTooltip>
			</Box>
			<ShiftTimesEditor
				scope="role"
				rowKey="default"
				hours={formData.partialBlockShifts.default ?? []}
				fullShiftTimes={formData.fullShiftTimes.default ?? []}
				hourOptions={hourOptions}
				setFormData={setFormData}
				dataTestId="dept-config-shifts-default-editor"
			/>
		</Box>
	);
};
